import React, { useState, useEffect, useRef } from 'react';
import { Input, Form, InputNumber, Select, Tooltip, DatePicker as AntDatePicker, 
    TreeSelect, Card, Alert } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import useSWR from 'swr';
import ObjectID from 'bson-objectid';

import { moneyFormatter, nodesTitleResolver, nodeTitleResolver, currencyResolver, useHandleSWR, momentFromEntry, datePartsFromMoment } from '~/libs/helpers';
import TagInput from '~/components/common/TagInput';
import CreateProjectModal from '~/components/modals/CreateProjectModal';
import { Accounts, Currencies } from '~/libs/constants';
import { InfoBadge } from '~/components/common/InfoBadge';
import { InputAddIcon, InputMinusIcon, HorizontalSeparator, YellowCheckbox,
  FormHorizontalFields, JournalEntryButtonContainer } from '~/components/common/EditorStyles';

const ExpenseEditor = (props) => {
  const { expense, onExpenseUpdate } = props;
  const [ modal, setModal ] = useState();
  const { data: swrAccount, mutate: mutateAccount } = useSWR('/api/account');
  const { data: account, busy: busyAccount } = useHandleSWR(swrAccount, [], true);
  const settings = account?.settings;
  const categories = settings?.categories || [];
  const paymentMethods = settings?.paymentMethods || [];
  const projects = settings?.projects || [];
  const currency = currencyResolver(expense.currency);
  const payments = expense.payments ? expense.payments : [];
  const isExpert = expense.isExpert;
  const isReadOnly = expense.isPaid && expense.isExpert;

  // If not "Expert" we only have one entry (the only one payment)
  const singleEntry = !isExpert && expense.payments && expense.payments.length > 0 ? expense.payments[0] : null;

  // Calculate totals (payments, and net)
  const totalPaymentsAmount = payments.reduce((a, b) => a + b.amount, 0);
  const totalAmount = expense.amount + expense.jctAmount + expense.withheldAmount;

  // Auto-refresh the amount of JCT if the JCT % changes, or if the brut amount changes.
  const isFirstRunOfJctAmountRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRunOfJctAmountRefresh.current) {
      isFirstRunOfJctAmountRefresh.current = false;
      return;
    }
    const jctAmount = Math.round(expense.amount * (expense.jctTax / 100));
    onExpenseUpdate({ ...expense, jctAmount });
  }, [expense.amount, expense.jctTax]);

  // Auto-refresh the amount of Withheld if the Withheld % changes.
  const isFirstRunOfWithheldAmountRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRunOfWithheldAmountRefresh.current) {
      isFirstRunOfWithheldAmountRefresh.current = false;
      return;
    }
    const withheldAmount = -1 * Math.round(expense.amount * (expense.withheldTax / 100));
    onExpenseUpdate({ ...expense, withheldAmount });
  }, [expense.amount, expense.withheldTax]);

  // Auto-refresh the amount of Withheld and JCT if the brut amount changes.
  const isFirstRunOfAmountRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRunOfAmountRefresh.current) {
      isFirstRunOfAmountRefresh.current = false;
      return;
    }
    const jctAmount = Math.round(expense.amount * (expense.jctTax / 100));
    const withheldAmount = -1 * Math.round(expense.amount * (expense.withheldTax / 100));
    onExpenseUpdate({ ...expense, jctAmount, withheldAmount });
  }, [expense.amount]);

  // If there are no payments yets, let's create defaults payments
  useEffect(() => {
    if (paymentMethods?.length < 1) {
      return;
    }
    if (!totalAmount) {
      return;
    }
    const now = new Date();
    if (!isExpert && !singleEntry) {
      let freshPayments = [{
        _id: ObjectID(),
        date: expense.date,
        year: expense.year,
        month: expense.month,
        day: expense.day,
        creditSubAccountId: paymentMethods[0]._id,
        amount: expense.amount,
      }];
      onExpenseUpdate({ ...expense, payments: freshPayments });
    }
    // Create a payment if there is no payment but marked as paid.
    else if (payments.length === 0 && expense.isPaid) {
      let freshPayments = [...payments];
      freshPayments.push({ _id: ObjectID(), _new: true, date: new Date(), 
        year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate(),
        creditSubAccountId: paymentMethods[0]._id, amount: totalAmount });
        freshPayments = freshPayments.sort((a, b) => a.date - b.date);
      onExpenseUpdate({ ...expense, payments: freshPayments });
    }
    // If only one payment and if it's paid OR the payment is above the total amount,
    else if (payments.length === 1 && expense.isPaid || singleEntry) {
      payments[0].amount = totalAmount;
      payments[0].date = expense.date;
      payments[0].year = expense.year;
      payments[0].month = expense.month;
      payments[0].day = expense.day;
      onExpenseUpdate({ ...expense, isPaid: true, payments: payments });
    }
  }, [payments, expense.amount, expense.date, paymentMethods]);

  const onAddProject = () => {
    setModal('createProject');
  }

  /* CATEGORIES */

  const mapCategoryToTree = (items) => {
    return items.map(x => ({
      key: x._id,
      value: x._id,
      title: nodesTitleResolver(x._id, categories),
      items: (x.items && x.items.length > 0) ? mapCategoryToTree(x.items) : null
    }));
  }
  let categoryData = mapCategoryToTree(categories);

  const onChangeCategory = (value) => {
    onExpenseUpdate({...expense, categoryId: value });
  }

  /* PAYMENT METHODS */

  const mapPaymentMethodToTree = (items) => {
    return items.map(x => ({
      key: x._id,
      value: x._id,
      disabled: x.name === 'personal-advance' && expense.personal,
      title: nodesTitleResolver(x._id, paymentMethods),
      items: (x.items && x.items.length > 0) ? mapPaymentMethodToTree(x.items) : null
    }));
  }
  let paymentMethodData = mapPaymentMethodToTree(paymentMethods);

  /* PROJECTS */

  const mapProjectToTree = (items) => {
    return items.map(x => ({
      key: x._id,
      value: x._id,
      title: nodeTitleResolver(projects, x._id),
      items: (x.items && x.items.length > 0) ? mapPaymentMethodToTree(x.items) : null
    }));
  }
  let projectData = mapProjectToTree(projects);

  /* ENTRIES RELATED FUNCTIONS */

  const onEntryUpdate = (entry) => {
    let freshPayments = [...expense.payments.filter(x => x._id !== entry._id), entry];
    freshPayments = freshPayments.sort((a, b) => a.date - b.date);
    const freshInvoice = { ...expense, payments: freshPayments };
    onExpenseUpdate(freshInvoice);
  }

  const onAddRelatedEntry = () => {
    const now = new Date();
    let freshPayments = [...(expense.payments ? expense.payments : []), {
      _id: ObjectID(),
      date: new Date(),
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      creditSubAccountId: paymentMethods[0]._id,
      amount: totalAmount - totalPaymentsAmount,
    }];
    freshPayments = freshPayments.sort((a, b) => a.date - b.date);
    onExpenseUpdate({ ...expense, payments: freshPayments });
  }

  const onRemoveRelatedEntry = (entry) => {
    const freshPayments = [...expense.payments.filter(x => x._id !== entry._id)];
    const freshInvoice = {...expense, payments: freshPayments };
    onExpenseUpdate(freshInvoice);
  }

  return (
    <Form layout="vertical">

      <FormHorizontalFields>

        <Form.Item label={<span>Category <InfoBadge tooltip='The type of this expense.' /> </span>}>
          <TreeSelect treeData={categoryData} value={expense.categoryId}
            showSearch={true} treeDefaultExpandAll={true}
            filterTreeNode={(input, treeNode) => {
              input = input.toLowerCase();
              return treeNode.title.toLowerCase().includes(input);
            }}
            onChange={onChangeCategory} disabled={expense.personal}>
          </TreeSelect>
        </Form.Item>

        <Form.Item label={<span>Advance Payment <InfoBadge tooltip='This marks the expense has an advance for a project, or for yourself.' /> </span>} style={{ marginLeft: 20 }}>
          <Tooltip title={<span>Check <i>billable</i> if you advanced that expense for a project.</span>}>
            <YellowCheckbox checked={expense.billable} 
              onChange={(ev) => onExpenseUpdate({...expense, billable: ev.target.checked,
                projectId: ev.target.checked ? expense.projectId : null 
              })}>
              Billable
            </YellowCheckbox>
          </Tooltip>
          <Tooltip title={<span>Check <i>personal</i> if this expense is, in fact, a personal one.</span>}>
            <YellowCheckbox checked={expense.personal} 
              onChange={(ev) => onExpenseUpdate({...expense, personal: ev.target.checked,
                categoryId: ev.target.checked ? null : expense.categoryId 
              })}>
              Personal
            </YellowCheckbox>
          </Tooltip>
        </Form.Item>

      </FormHorizontalFields>

      <div style={{ marginTop: -20, marginBottom: 20 }}>

        {expense.withheldTax > 0 && <FormHorizontalFields>
          <div></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>Withholding Tax</div>
          <div style={{ marginLeft: 20 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <InputNumber value={expense.withheldAmount ? parseFloat(expense.withheldAmount) : null} 
                disabled={isReadOnly}
                placeholder="Amount" step={currency.steps} precision={currency.decimals}
                formatter={moneyFormatter}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                onChange={(value) => onExpenseUpdate({...expense, withheldAmount: value })} />          
              <Select value={expense.withheldTax} disabled={isReadOnly} 
                onChange={(value) => onExpenseUpdate({...expense, withheldTax: value })}>
                <Select.Option value={0}>None</Select.Option>
                <Select.Option value={10.21}>10.21%</Select.Option>
                <Select.Option value={20.42}>20.42%</Select.Option>
              </Select>
            </Input.Group>
          </div>
        </FormHorizontalFields>}

      </div>

      <FormHorizontalFields>

        <Form.Item label={<span>Date <InfoBadge tooltip={<span>The date of the expense. If the payment date is different, you must set this expense as an <i>Accrued Expense</i>.</span>} /> </span>}>
          <AntDatePicker value={momentFromEntry(expense)}
            onChange={(value) => { 
              const { year, month, day } = datePartsFromMoment(value);
              onExpenseUpdate({...expense, date: value.toDate(), year, month, day }); 
            }} />
        </Form.Item>

        <Form.Item label={<span>Details <InfoBadge tooltip='This is usually the name of your provider. You can add additional information such as the month of the expense, a reference, etc.' /> </span>}>
          <Input value={expense.details} onChange={(ev) => onExpenseUpdate({...expense, details: ev.target.value })} />
        </Form.Item>

        <Form.Item label={<span>Total Amount {expense.jctTax > 0 ? '& JCT' : ''}<InfoBadge tooltip='???' /> </span>} style={{ display: 'flex' }}>
          <Input.Group compact style={{ display: 'flex' }}>

            {/* <InputNumber value={totalAmount} disabled={true} precision={currency.decimals}
              formatter={moneyFormatter} parser={value => value.replace(/\$\s?|(,*)/g, '')}
            /> */}

            <InputNumber value={expense.amount ? parseFloat(expense.amount) : null} disabled={isReadOnly}
              placeholder="Amount" step={currency.steps} precision={currency.decimals}
              formatter={moneyFormatter}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              onChange={(value) => onExpenseUpdate({...expense, amount: value })} />
            <Select value={expense.currency} disabled={isReadOnly} 
              onChange={(value) => onExpenseUpdate({...expense, currency: value })}>
              {Currencies.map(x => <Select.Option key={'expense-currency-' + x.code} value={x.code}>{x.code}</Select.Option>)}
            </Select>


            {expense.jctTax > 0 && <Select value={expense.jctTax} disabled={isReadOnly} 
              onChange={(value) => onExpenseUpdate({...expense, jctTax: value })}>
              <Select.Option value={0}>None</Select.Option>
              <Select.Option value={8}>8%</Select.Option>
              <Select.Option value={10}>10%</Select.Option>
            </Select>}
          </Input.Group>
          {expense.jctTax > 0 && <small style={{ fontSize: 12 }}>
            {currency.symbol + moneyFormatter(parseFloat(expense.jctAmount))} of JCT included.
          </small>}
        </Form.Item>

        {singleEntry && <Form.Item label={<span>Method <InfoBadge tooltip='How the payment was made.' /> </span>}>
          <TreeSelect treeData={paymentMethodData} value={singleEntry.creditSubAccountId} disabled={isReadOnly}
            showSearch={true} treeDefaultExpandAll={true}
            filterTreeNode={(input, treeNode) => {
              input = input.toLowerCase();
              return treeNode.title.toLowerCase().includes(input);
            }}
            onChange={(value) => { onEntryUpdate({...singleEntry, creditSubAccountId: value }); }}>
          </TreeSelect>
        </Form.Item>}

      </FormHorizontalFields>

      {expense?.isExpert && <Card style={{ marginBottom: 20, background: '#f9f9f9' }} size="small"
        title={<span style={{ fontSize: 18 }}>
        <span>
          Payments
        </span>
        <span style={{ marginLeft: 20 }}>
          {!expense.isPaid && <InputAddIcon onClick={() => onAddRelatedEntry()} />}
        </span>
       </span>}>

        {payments.map((entry, i) => 
          <FormHorizontalFields key={entry._id} style={{ marginBottom: -20 }}>

            <Form.Item label={i === 0 && <span>Date <InfoBadge tooltip={<span>???</span>} /> </span>}>
              <AntDatePicker value={momentFromEntry(entry)} disabled={isReadOnly} allowClear={false}
                onChange={(value) => { 
                  const { year, month, day } = datePartsFromMoment(value);
                  onEntryUpdate({...entry, year, month, day }); 
                }} />
            </Form.Item>

            <Form.Item label={i === 0 && <span>Method <InfoBadge tooltip='How the payment was made.' /> </span>}>
              <TreeSelect treeData={paymentMethodData} value={entry.creditSubAccountId} disabled={isReadOnly}
                showSearch={true} treeDefaultExpandAll={true}
                filterTreeNode={(input, treeNode) => {
                  input = input.toLowerCase();
                  return treeNode.title.toLowerCase().includes(input);
                }}
                onChange={(value) => { onEntryUpdate({...entry, creditSubAccountId: value }); }}>
              </TreeSelect>
            </Form.Item>

            <Form.Item label={i === 0 && <span>Amount <InfoBadge tooltip='???' /> </span>}>
              <Input.Group compact style={{ display: 'flex' }}>
                <InputNumber value={parseFloat(entry.amount ? entry.amount : 0)} disabled={isReadOnly}
                  placeholder="Amount" step={currency.step} precision={currency.decimals}
                  formatter={moneyFormatter}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  onChange={(value) => onEntryUpdate({...entry, amount: value })} />
              </Input.Group>
            </Form.Item>
            
            <JournalEntryButtonContainer>
              {!expense.isPaid && <InputMinusIcon onClick={() => onRemoveRelatedEntry(entry)} />}
            </JournalEntryButtonContainer>

          </FormHorizontalFields> 
        )}

        {!expense.isPaid && totalPaymentsAmount === totalAmount && <Alert message="It seems like this expense was completely paid. If so, please change the status to 'Paid'." type="success" />}

        {totalPaymentsAmount > totalAmount && <Alert message={<span>The total of the payments ({currency.symbol}{moneyFormatter(totalPaymentsAmount)}) is superior to the total amount ({currency.symbol}{moneyFormatter(totalAmount)}).</span>} type="error" />}

        {totalPaymentsAmount < totalAmount && <Alert message={<span>Your still owe {currency.symbol}{moneyFormatter(totalAmount - totalPaymentsAmount)}.</span>} type="error" />}

      </Card>}

      {expense.billable && 
        <Card style={{ marginBottom: 20 }} size="small" 
          title={<span style={{ fontSize: 18 }}><span>Billable To</span></span>}>        
          <FormHorizontalFields>
            <Form.Item label={<span>Project <InfoBadge tooltip='The project this expense is related to.' /></span>}>
              <TreeSelect treeData={projectData} value={expense.projectId} disabled={isReadOnly}
                showSearch={true} treeDefaultExpandAll={true} suffixIcon={<InputAddIcon onClick={onAddProject} />}
                filterTreeNode={(input, treeNode) => {
                  input = input.toLowerCase();
                  return treeNode.title.toLowerCase().includes(input);
                }}
                onChange={(value) => { onExpenseUpdate({...expense, projectId: value }); }}>
              </TreeSelect>
            </Form.Item>
          </FormHorizontalFields>
        </Card>
      }

      <FormHorizontalFields>
        <Form.Item label={<span>Notes <InfoBadge tooltip='Notes are for your eyes only, and exclusively available in Tanukin.' /> </span>}>
          <TextArea onChange={(ev) => onExpenseUpdate({...expense, notes: ev.target.value })} />
        </Form.Item>

        <Form.Item label={<span>Tags <InfoBadge tooltip={<span>Tags are optional, but allows expenses to be searched and grouped really easily.</span>} /> </span>}>
          <TagInput value={expense.tags} onChange={(tags) => onExpenseUpdate({...expense, tags })} />
        </Form.Item>
      </FormHorizontalFields>

      <CreateProjectModal visible={modal === 'createProject'} 
        onChange={(value) => onExpenseUpdate({...expense, projectId: value })}
        onClose={() => setModal()} />
      
    </Form>
  );
}

export default ExpenseEditor;